import type { FsId } from '@fullstory/recording-api';
import type { NormalizedTagName } from '@fullstory/rewriters';
import { renderMasked } from '@fullstory/shared/masktext';
import { TEXT_NODE_TAG } from '@fullstory/shared/node_decoder';
import { isEqual } from 'lodash';
import { DirtyBit } from './dirtybits';
import { FsStyleElement } from './fscssomelement';
import type { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';

/**
 * The vDOM representation of a {@link Text} node.
 *
 * TODO(sethf): The CSS rewriting code in this class does a number of awkward
 * things that are a consequence of handling CSS rewriting here instead of in
 * FsStyleElement, where it would naturally belong. A clean implementation is
 * complicated by unbuffered mode, so we'll keep things as they are a little
 * longer until we're ready to rip unbuffered mode out.
 */
export class FsText extends FsNode {
  private _lastSetTextArgs: { text: string; skipRewrite: boolean } | undefined = undefined;
  private _textContent: string | null = null;
  private _skipTextContentRewrite: boolean = true;

  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, TEXT_NODE_TAG as NormalizedTagName, undefined, fsid);
  }

  isText(): this is FsText {
    return true;
  }

  setText(text: string, skipRewrite: boolean = false): void {
    const args = { text, skipRewrite };
    if (isEqual(args, this._lastSetTextArgs)) {
      return; // Nothing has changed.
    }

    this._lastSetTextArgs = args;
    this._textContent = renderMasked(text);
    this._skipTextContentRewrite = skipRewrite;
    this.setDirtyBit(DirtyBit.Text);
  }

  getTextContent(): string | null {
    return this._textContent;
  }

  protected createNode(doc: Document): Text {
    return doc.createTextNode('');
  }

  protected flushProperties(dom: Text): void {
    super.flushProperties(dom);

    if (this._hasDirtyBit(DirtyBit.Text)) {
      this._flushTextContent();
    }
  }

  protected invalidateProperties(): void {
    super.invalidateProperties();

    if (typeof this._textContent === 'string') {
      this.setDirtyBit(DirtyBit.Text);
    }
  }

  private _flushTextContent(): void {
    // Rewrite textContent for style tags.
    const content = this._textContent;
    if (this.parent instanceof FsStyleElement && content != null && !this._skipTextContentRewrite) {
      const parent = this.parent;
      if (!parent.cssRewritingFailed) {
        // try rewriting a single node
        const { value, success } = this.ownerDoc.inlineCssRewriter.rewriteStyleSheetText(content);
        if (!success && content.trim() !== '') {
          // switch to block-level rewriting and try again
          console.error(
            'Failed to rewrite an individual CSS text node. Switching to whole-block rewriting.',
          );
          parent.cssRewritingFailed = true;
          this._flushTextContent();
          return; // all processing was done in the previous line
        } else {
          this.dom!.textContent = value;
        }
      } else {
        // at least one node cannot be rewritten on it's own: always rewrite the whole block instead
        const texts: string[] = [];
        for (let node = parent.firstChild; node != null; node = node.next) {
          if (!node.isText()) {
            continue;
          }
          texts.push(node._textContent ?? '');
        }
        const rewrittenText = this.ownerDoc.inlineCssRewriter.rewriteStyleSheetText(
          texts.join(''),
        ).value;
        parent.firstChild!.dom!.textContent = rewrittenText;
        for (let node = parent.firstChild!.next; node != null; node = node.next) {
          node.dom!.textContent = '';
          node._clearDirtyBit(DirtyBit.Text);
        }
      }
    } else {
      this.dom!.textContent = this._textContent;
    }

    this._clearDirtyBit(DirtyBit.Text);
  }
}
